import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

// Supabase project URL for building calendar feed URL
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

/**
 * Hook for managing calendar sync functionality
 * Handles token generation, URL building, and clipboard operations
 *
 * @param {string} userId - User ID
 * @returns {Object} - Calendar sync state and actions
 */
export function useCalendarSync(userId) {
  const [calendarToken, setCalendarToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch existing calendar token from user profile
   */
  const fetchToken = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('user_profiles')
        .select('calendar_token')
        .eq('id', userId)
        .single();

      if (fetchError) throw fetchError;

      setCalendarToken(data?.calendar_token || null);
    } catch (err) {
      console.error('[useCalendarSync] Error fetching token:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /**
   * Generate or regenerate calendar token
   */
  const generateToken = useCallback(async () => {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    setGenerating(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase.rpc(
        'generate_calendar_token',
        { p_user_id: userId }
      );

      if (rpcError) throw rpcError;

      setCalendarToken(data);
      return { success: true, token: data };
    } catch (err) {
      console.error('[useCalendarSync] Error generating token:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setGenerating(false);
    }
  }, [userId]);

  /**
   * Revoke calendar token (set to null)
   */
  const revokeToken = useCallback(async () => {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    setGenerating(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ calendar_token: null })
        .eq('id', userId);

      if (updateError) throw updateError;

      setCalendarToken(null);
      return { success: true };
    } catch (err) {
      console.error('[useCalendarSync] Error revoking token:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setGenerating(false);
    }
  }, [userId]);

  /**
   * Build the calendar feed URL
   */
  const getCalendarUrl = useCallback((protocol = 'https') => {
    if (!calendarToken) return null;

    // Extract project ref from Supabase URL
    // URL format: https://PROJECT_REF.supabase.co
    const projectRef = SUPABASE_URL?.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1];

    if (!projectRef) {
      console.error('[useCalendarSync] Could not extract project ref from URL');
      return null;
    }

    const baseUrl = `${projectRef}.supabase.co/functions/v1/calendar-feed`;

    if (protocol === 'webcal') {
      return `webcal://${baseUrl}?token=${calendarToken}`;
    }

    return `https://${baseUrl}?token=${calendarToken}`;
  }, [calendarToken]);

  /**
   * Copy URL to clipboard
   */
  const copyToClipboard = useCallback(async (protocol = 'https') => {
    const url = getCalendarUrl(protocol);
    if (!url) {
      return { success: false, error: 'No calendar URL available' };
    }

    try {
      await navigator.clipboard.writeText(url);
      return { success: true };
    } catch (err) {
      console.error('[useCalendarSync] Clipboard error:', err);
      return { success: false, error: 'Failed to copy to clipboard' };
    }
  }, [getCalendarUrl]);

  /**
   * Open subscription URL directly (for Outlook/calendar apps)
   */
  const openSubscription = useCallback(() => {
    const url = getCalendarUrl('webcal');
    if (url) {
      window.open(url, '_blank');
      return { success: true };
    }
    return { success: false, error: 'No calendar URL available' };
  }, [getCalendarUrl]);

  // Fetch token on mount
  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  return {
    // State
    calendarToken,
    loading,
    generating,
    error,
    hasToken: !!calendarToken,

    // URLs
    httpsUrl: getCalendarUrl('https'),
    webcalUrl: getCalendarUrl('webcal'),

    // Actions
    generateToken,
    revokeToken,
    copyToClipboard,
    openSubscription,
    refetch: fetchToken
  };
}
